import { Alert, Button, Empty, Flex, Skeleton, Switch, Tooltip, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  getDesktopNotificationPermission,
  requestDesktopNotificationPermission
} from "../../../../../utils/notification/browser-notification.ts"
import { subscribeToWebPush } from "../../../../../utils/notification/web-push.ts"
import type { NotificationSettingsItem } from "../../../../Notifications/types/notification.types.ts"

const { Text } = Typography

// The four channels this row actually persists — matches
// `NotificationSettingsItemSerializer` on the backend (`ezbuild_back`
// `notifications_app/serializers.py`) exactly: `email`, `in_app`, `phone`
// (labeled "Push" — FCM to the mobile app, see
// `NotificationService.send_via_channels`/`send_push_notification_task`),
// and `desktop` (Web Push to a subscribed browser, via `pywebpush` +
// `send_web_push_notification_task` — a distinct delivery path from mobile
// Push, never the same channel under two names). `whatsapp` also exists on
// the model but is a fifth channel nobody asked to expose here yet — left
// out on purpose, not missed.
export type CalendarNotificationChannel = "email" | "in_app" | "phone" | "desktop"
export type CalendarNotificationTypeDraft = Record<CalendarNotificationChannel, boolean>

const CHANNELS: CalendarNotificationChannel[] = ["in_app", "desktop", "phone", "email"]

interface CalendarNotificationTypesTableProps {
  items: NotificationSettingsItem[] | null
  draft: Record<number, CalendarNotificationTypeDraft>
  onChange: (
    notificationType: number,
    channel: CalendarNotificationChannel,
    value: boolean
  ) => void
  loading: boolean
  error: boolean
  onRetry: () => void
}

// The Calendar Settings drawer's own view of the app-wide `CALENDAR`
// notification group (see NotificationSettingsForm.tsx) — same rows, same
// persistence, deliberately not the same UI: no per-column bulk switches, no
// Collapse chrome, styled to sit inside this drawer rather than read as a
// pasted fragment of the global Settings page.
export function CalendarNotificationTypesTable({
  items,
  draft,
  onChange,
  loading,
  error,
  onRetry
}: CalendarNotificationTypesTableProps) {
  const { t } = useTranslation()

  if (loading) {
    return <Skeleton active title={false} paragraph={{ rows: 4 }} />
  }

  if (error) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t("notifications.settings.loadError")}
      >
        <Button size="small" onClick={onRetry}>
          {t("notifications.settings.retry")}
        </Button>
      </Empty>
    )
  }

  if (!items || items.length === 0) return null

  // Read once per render, not tracked live — the same simple approach
  // `CalendarAddReminderOffsetField` already uses. The browser's own
  // permission dialog is the only thing that changes this while the app is
  // open, and it always re-renders the page it was triggered from.
  const desktopPermission = getDesktopNotificationPermission()
  const desktopBlocked =
    desktopPermission === "denied" || desktopPermission === "unsupported"

  const handleChange = (
    notificationType: number,
    channel: CalendarNotificationChannel,
    checked: boolean
  ) => {
    onChange(notificationType, channel, checked)
    if (channel !== "desktop" || !checked) return
    // The stored preference can be set even before permission exists (or
    // while it's denied) — that's a real, distinct state, not an error — but
    // turning it on for the first time is the right moment to actually ask,
    // mirroring the same reminder-field prompt elsewhere in Calendar. Either
    // way, the preference alone delivers nothing without a live
    // `PushSubscription` row — `subscribeToWebPush()` is idempotent
    // (returns the existing subscription rather than duplicating it), so
    // it's safe to call whenever permission is already granted too.
    if (desktopPermission === "granted") {
      void subscribeToWebPush()
    } else if (desktopPermission === "default") {
      void requestDesktopNotificationPermission().then((permission) => {
        if (permission === "granted") void subscribeToWebPush()
      })
    }
  }

  return (
    <Flex vertical gap={8}>
      {desktopBlocked && (
        <Alert
          type="warning"
          showIcon
          message={
            desktopPermission === "unsupported"
              ? "This browser doesn't support desktop notifications."
              : "Desktop notifications are blocked in this browser. Allow them in your browser's site settings to receive Calendar desktop alerts."
          }
        />
      )}
      <div className="overflow-hidden rounded-md border border-gray-200">
        <div className="grid grid-cols-[2.4fr_0.9fr_0.9fr_0.9fr_0.9fr] items-center gap-1 border-b border-gray-200 bg-gray-50 px-2 py-2">
          <span />
          {CHANNELS.map((channel) => (
            <Text
              key={channel}
              type="secondary"
              className="whitespace-nowrap text-center text-[11px]"
            >
              {t(`notifications.channels.${channel}`)}
            </Text>
          ))}
        </div>
        {items.map((item) => {
          const row = draft[item.notification_type] ?? item
          return (
            <div
              key={item.notification_type}
              className="grid grid-cols-[2.4fr_0.9fr_0.9fr_0.9fr_0.9fr] items-center gap-1 border-b border-gray-100 px-2 py-2 last:border-b-0"
            >
              <Text className="text-sm">{item.notification_type_name}</Text>
              {CHANNELS.map((channel) => {
                const label = `${item.notification_type_name} ${t(`notifications.channels.${channel}`)}`
                const blocked = channel === "desktop" && desktopBlocked
                const control = (
                  <Switch
                    size="small"
                    checked={row[channel]}
                    onChange={(checked) => handleChange(item.notification_type, channel, checked)}
                    aria-label={blocked ? `${label} (requires browser permission)` : label}
                  />
                )
                return (
                  <Flex key={channel} justify="center">
                    {blocked ? (
                      <Tooltip title="Requires browser permission">{control}</Tooltip>
                    ) : (
                      control
                    )}
                  </Flex>
                )
              })}
            </div>
          )
        })}
      </div>
    </Flex>
  )
}
